import { useSearchParams } from "react-router";
import {
  getParamValue,
  updateParamValue,
  usePagePrefs,
} from "@pepsico/pref-permissions";

import { TAB_OPEN_PREF_KEY, TAB_PREF_KEY, type SidePanelTab } from "defs";

const SIDE_PANEL_TABS: readonly SidePanelTab[] = [
  "ovr",
  "bnd",
  "img",
  "plt",
  "anl",
];

const isSidePanelTab = (value: string | null): value is SidePanelTab =>
  value != null && SIDE_PANEL_TABS.includes(value as SidePanelTab);

interface SetTabPrefsOptions {
  isOpen?: boolean;
  tab?: SidePanelTab;
}

interface SidePanelTabPrefs {
  activeTab: SidePanelTab;
  isTabOpen: boolean;
  setTabPrefs: (options: SetTabPrefsOptions) => void;
}

/** Reads/writes the side panel `tab` and `tabopen` prefs. */
export const useSidePanelTabPrefs = (): SidePanelTabPrefs => {
  const [prefs, isReady] = usePagePrefs();
  const [, setSearchParams] = useSearchParams();

  const tabPref = getParamValue(prefs, TAB_PREF_KEY);

  const setTabPrefs = ({ isOpen, tab }: SetTabPrefsOptions) => {
    if (!isReady) {
      return;
    }

    setSearchParams(
      (prev) => {
        let next = prev;
        if (tab != null) {
          next = updateParamValue(next, TAB_PREF_KEY, prefs, tab);
        }
        if (isOpen != null) {
          next = updateParamValue(
            next,
            TAB_OPEN_PREF_KEY,
            prefs,
            isOpen ? "Y" : "N",
          );
        }

        return next;
      },
      { replace: true, state: { inPageNav: true } },
    );
  };

  return {
    activeTab: isSidePanelTab(tabPref) ? tabPref : "ovr",
    isTabOpen: getParamValue(prefs, TAB_OPEN_PREF_KEY) === "Y",
    setTabPrefs,
  };
};
